import { cn } from '../lib/utils'

/**
 * The Buildtory mark — a placeholder, deliberately simple so a commissioned
 * logo can replace it without touching a single call site (spec 2026-09-17-02).
 *
 * A rounded square filled with `--gradient-brand` carrying three stacked white
 * slabs of decreasing width: a "B" that reads as floors. The gradient comes
 * from the CSS token rather than being baked in, so the mark follows the
 * theme. `public/logo-mark.svg` is the same drawing with the light gradient
 * baked in, for the favicon and PWA icon set (`pnpm icons:build`).
 */

/** Radius is 26 % of the side, at every size. */
export const LOGOMARK_RADIUS_RATIO = 0.26
/** The glyph occupies 75 % of the tile. */
export const LOGOMARK_GLYPH_RATIO = 0.75

export const LOGOMARK_PATH =
  'M5 4h9a3 3 0 0 1 0 6H5zM5 11h12a3 3 0 0 1 0 6H5zM5 18h7a1 1 0 0 1 0 2H5z'

export interface LogomarkProps {
  /** Side of the tile in px. */
  size?: number
  className?: string
  /** Rendered as an `sr-only` span; without it the mark is `aria-hidden`. */
  label?: string
  'data-testid'?: string
}

export function Logomark({
  size = 32,
  className,
  label,
  'data-testid': testId,
}: LogomarkProps) {
  const glyph = Math.round(size * LOGOMARK_GLYPH_RATIO)
  return (
    <span
      aria-hidden={label ? undefined : true}
      className={cn(
        'inline-flex shrink-0 items-center justify-center bg-gradient-brand text-white shadow-tile',
        className
      )}
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * LOGOMARK_RADIUS_RATIO),
      }}
      data-testid={testId}
    >
      <svg
        viewBox="0 0 24 24"
        width={glyph}
        height={glyph}
        fill="currentColor"
        style={{ filter: 'drop-shadow(0 1px 0 oklch(0 0 0 / 12%))' }}
      >
        <path d={LOGOMARK_PATH} />
      </svg>
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  )
}
